<template>
    <teleport to="body">
        <div v-if="currentVendorItems.length">
            <!-- Floating bottom basket preview -->
            <transition name="slide-up">
                <div v-if="!expanded" class="basket-preview animated slide-in-up pb-4"
                     :class="{ 'basket-pulse': basketJustUpdated }"
                     @click="expanded = true">
                    <div class="basket-summary">
                        <strong>{{ currentVendorItems.length }} item<span v-if="currentVendorItems.length > 1">s</span></strong>
                        £{{ totalAmount.toFixed(2) }}
                        <span class="float-end">View Basket</span>
                    </div>
                </div>
            </transition>

            <!-- Expanded basket overlay -->
            <transition name="slide-up">
                <div v-if="expanded" class="basket-expanded text-main">
                    <div class="basket-header d-flex justify-content-between align-items-center px-3 pt-3">
                        <h4 class="mb-0 col-11 text-main">Your Basket</h4>
                        <button @click="expanded = false" class="btn-close btn btn-sm btn-light"></button>
                    </div>
                    <hr />
                    <div class="basket-body px-4">
                        <div class="basket-item" v-for="item in currentVendorItems" :key="item.itemId">
                            <div class="basket-item-header">
                                <div class="col-11">
                                    <strong class="item-name">{{ item.item.itemName }}</strong>

                                </div>
                                <button @click="removeFromCart(item)" class="remove-button"><i class="fa fa-trash"></i></button>
                            </div>

                            <div v-if="item.options?.length" class="basket-options">
                                <div v-for="opt in item.options" :key="opt.optionId" class="basket-option-line">
                                    <span>
                                        {{ getOptionLabel(item.item.options, opt.optionId) }}:
                                        <strong>{{ getChoiceLabel(item.item.options, opt.optionId, opt.choiceId) }}</strong>
                                        <span v-if="getChoicePrice(item.item.options, opt.optionId, opt.choiceId) > 0">
                                            (+&pound;{{ getChoicePrice(item.item.options, opt.optionId, opt.choiceId).toFixed(2) }})
                                        </span>
                                    </span>
                                </div>
                            </div>
                            <div v-if="item.dietaryRequirements?.length" class="basket-options">
                                <div class="basket-option-line">
                                    <span>
                                        Dietary preferences:
                                        <strong>{{ item.dietaryRequirements.join(', ') }}</strong>
                                    </span>
                                </div>
                            </div>
                            <div class="d-flex justify-content-between align-items-center">
                                <div class="item-qty d-flex align-items-center gap-2 py-2">
                                    <button class="qty-button" @click="decrementItem(item)">−</button>
                                    <span>{{ item.quantity }}</span>
                                    <button class="qty-button" @click="incrementItem(item)">+</button>
                                </div>
                            </div>
                            <div class="basket-price">
                                &pound;{{ calculateItemTotal(item).toFixed(2) }}
                            </div>
                        </div>

                        <div class="basket-total mt-3">
                            <div>Items Total: &pound;{{ itemSubtotal.toFixed(2) }}</div>
                            <div v-if="actualDeliveryFee > 0">Delivery Fee: &pound;{{ actualDeliveryFee.toFixed(2) }}</div>
                            <div v-else class="free-delivery">Free Delivery</div>
                            <hr />
                            <div><strong>Total: &pound;{{ totalAmount.toFixed(2) }}</strong></div>
                        </div>
                        <h5 class="text-dark">Order request</h5>
                        <div>
                            <input v-model="notes"
                                   required
                                   class="form-input"
                                   placeholder="please enter additional details here like, No Onions"
                                    />
                        </div>
                        <div class="checkout-address my-3" v-if="!isCollection">

                            <h5 class="text-dark">Delivery Address</h5>
                            <div>
                                <input v-model="address.address"
                                       required
                                       class="form-input"
                                       placeholder="Street Address"
                                       autocomplete="street-address"
                                       :class="{
                                          'is-invalid': addressErrors.address,
                                          'is-valid': address.address && !addressErrors.address
                                        }" />
                                <div v-if="addressErrors.address" class="invalid-feedback">
                                    {{ addressErrors.address }}
                                </div>
                            </div>
                            <div>
                                <input v-model="address.city"
                                       required
                                       class="form-input"
                                       placeholder="City"
                                       autocomplete="address-level2"
                                       :class="{
              'is-invalid': addressErrors.city,
              'is-valid': address.city && !addressErrors.city
            }" />
                                <div v-if="addressErrors.city" class="invalid-feedback">
                                    {{ addressErrors.city }}
                                </div>
                            </div>
                            <div>
                                <input v-model="address.postcode"
                                       required
                                       class="form-input"
                                       placeholder="Postcode"
                                       autocomplete="postal-code"
                                       :class="{
                                          'is-invalid': addressErrors.postcode,
                                          'is-valid': address.postcode && !addressErrors.postcode
                                        }" />
                                <div v-if="addressErrors.postcode" class="invalid-feedback">
                                    {{ addressErrors.postcode }}
                                </div>
                            </div>
                        </div>

                        <button @click="startCheckout" class="btn btn-success w-100 mb-3">Checkout</button>
                    </div>
                </div>
            </transition>
        </div>
        <LoginModal :visible="loginModalOpen" @close="loginModalOpen = false" :allowGuest="true" :allowSignup="true" :allowEmailLogin="true" />
    </teleport>
</template>


<script setup>
    import { storeToRefs } from 'pinia';
    import { useOrderStore } from '../../utils/orderStore';
    import { fetchWithAuth } from '../../utils/authFetch';
    import { useAuthStore } from '../../utils/authStore';
    import { ref, watch, computed, onMounted } from 'vue';
    import { LoginModal,  } from 'shared-vue'

    const expanded = ref(false);
    const basketJustUpdated = ref(false);
    const loginModalOpen = ref(false);
    
    import { reactive } from 'vue';

    const addressErrors = reactive({
        address: '',
        city: '',
        postcode: ''
    });

    function validateAddress() {
        addressErrors.address = address.value.address?.trim() ? '' : 'Street address is required';
        addressErrors.city = address.value.city?.trim() ? '' : 'City is required';
        addressErrors.postcode = address.value.postcode?.trim() ? '' : 'Postcode is required';

        // Return true if all fields are valid
        return !addressErrors.address && !addressErrors.city && !addressErrors.postcode;
    }
    const orderStore = useOrderStore();
    const authStore = useAuthStore();
    const props = defineProps({
        onProceedToCheckout: Function
    });
    const {
        currentVendorItems,
        itemSubtotal,
        totalAmount,
        actualDeliveryFee,
        vendor,
        address,
        showCheckout,
        referral
    } = storeToRefs(orderStore);
    const notes = ref('');
    const customerLocation = ref(getStoredLocation());

    function getStoredLocation() {
        return JSON.parse(localStorage.getItem('customerLocation') || '{}')
    }

    // Call this whenever you update localStorage externally
    function updateCustomerLocation() {
        customerLocation.value = getStoredLocation()
    }

    // Use this in your component
    const isCollection = computed(() => {
        const mode = customerLocation.value?.mode
        return mode === 'pickup'
    })

    const user = storeToRefs(authStore).user;

    async function removeFromCart(item) {
        await orderStore.removeFromCart(item);
        if (currentVendorItems.value.length == 0) {
            expanded.value = false;
        }
    }

    function getChoicePrice(options, optionId, choiceId) {
        const option = options?.find(o => o.id === optionId);
        const choice = option?.choices?.find(c => c.id === choiceId);
        return choice?.priceAdjustment ?? 0;
    }

    function getOptionLabel(options, optionId) {
        const option = options?.find(o => o.id === optionId);
        return option?.name ?? 'Option';
    }

    function getChoiceLabel(options, optionId, choiceId) {
        const option = options?.find(o => o.id === optionId);
        const choice = option?.choices?.find(c => c.id === choiceId);
        return choice?.label ?? 'Choice';
    }

    function calculateItemTotal(item) {
        let basePrice = item.item.price;
        let extra = 0;
        if (item.options?.length && item.item?.options?.length) {
            for (const opt of item.options) {
                const optionDef = item.item.options.find(o => o.id === opt.optionId);
                const choiceDef = optionDef?.choices.find(c => c.id === opt.choiceId);
                if (choiceDef?.priceAdjustment) {
                    extra += choiceDef.priceAdjustment;
                }
            }
        }
        return (basePrice + extra) * item.quantity;
    }

    async function startCheckout() {
        if (!user.value) {
            // Redirect or show login
            loginModalOpen.value = true;
            return;
        }
        if (isCollection || validateAddress()) {
            await createOrder();
        }
    }

    let lastItemCount = totalAmount.value
    let dontstackTimeout = null;
    watch(totalAmount, (totalAmount) => {
        const newCount = totalAmount
        console.log(totalAmount)
        if (newCount > lastItemCount && !expanded.value) {
            basketJustUpdated.value = true
            if (dontstackTimeout != null) {
                clearTimeout(dontstackTimeout)
            }
            // Remove the pulse after 1s
            dontstackTimeout = setTimeout(() => {
                basketJustUpdated.value = false
                dontstackTimeout = null;
            }, 1000);

        }
        lastItemCount = newCount
    })

    async function createOrder() {
        const vendorItems = currentVendorItems.value;
        if (!vendorItems.length) {
            alert("Please add items to your order.");
            return;
        }

        const orderItems = vendorItems.map(i => {
            const selectedOptions = [];
            if (i.options?.length && i.item?.options?.length) {
                for (const opt of i.options) {
                    const optionDef = i.item.options.find(o => o.id === opt.optionId);
                    const choiceDef = optionDef?.choices.find(c => c.id === opt.choiceId);
                    if (optionDef && choiceDef) {
                        selectedOptions.push({
                            name: optionDef.name,
                            choice: choiceDef.label,
                            priceAdjustment: choiceDef.priceAdjustment
                        });
                    }
                }
            }

            const dietary = i.dietaryRequirements || [];

            return {
                itemId: i.itemId,
                quantity: i.quantity,
                selectedOptions,
                dietaryRequirements: dietary
            };
        });


        const location = JSON.parse(localStorage.getItem('customerLocation') || '{}');
        if (!location.lat || !location.lng) return;

        const orderData = {
            vendorId: vendor.value.vId,
            items: orderItems,
            address: address.value.address,
            city: address.value.city,
            postcode: address.value.postcode,
            deliveryLat: location.lat,
            deliveryLng: location.lng,
            collection: location.mode === 'pickup',
            customerNotes: notes.value,
            referralCode: referral.value
        };

        const responseOrder = await fetchWithAuth("/publicorder/create-order", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(orderData)
        });

        const json = await responseOrder.json();
        orderStore.setOrderId(json.orderId);
        props.onProceedToCheckout?.();

        // You can emit or route to checkout here
    }

    function incrementItem(item) {
        orderStore.addToCart({
            itemId: item.itemId,
            item: item.item,
            quantity: 1,
            options: item.options,
            dietaryRequirements: item.dietaryRequirements
        });
    }

    function decrementItem(item) {
        if (item.quantity <= 1) {
            removeFromCart(item);
        } else {
            item.quantity -= 1;
        }
    }

    const location = getStoredLocation();
    onMounted(() => {
        if (!address.value.address) {
            address.value.address = location.address_line1 + ', ' + location.address_line2 || location.address || '';
        }
        if (!address.value.city) {
            address.value.city = location.city || '';
        }
        if (!address.value.postcode) {
            address.value.postcode = location.postcode || '';
        }
        // postcode can be added similarly if available
    });
</script>
<style scoped>

    .basket-container {
        background-color: rgba(255, 255, 255, 0.05);
        background: #410330;
        padding: 1.5rem;
        border-radius: 12px;
        color: #1a1a1a;
        margin-top: 2rem;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        color: white;
    }

    .basket-title {
        font-size: 1.5rem;
        font-weight: 600;
        margin-bottom: 1.25rem;
    }

    .basket-item {
        border-bottom: 1px solid rgba(255, 255, 255, 0.15);
        box-shadow: 0, 0, 0, white;
        padding: .75rem 0;
        margin-bottom: 15px;
    }

        .basket-item:last-child {
            border-bottom: none;
        }

    .basket-item-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
    }

    .item-name {
        font-weight: bold;
        font-size: 1rem;
    }

    .item-qty {
        font-size: 0.85rem;
    }

    .remove-button {
        background: none;
        border: none;
        color: #e74c3c;
        font-size: 1.3rem;
        font-weight: bold;
        cursor: pointer;
        padding: 0 10px 0 0;
        margin-left: auto;
        transition: transform 0.2s ease;
    }

        .remove-button:hover {
            transform: scale(1.1);
            color: #c0392b;
        }

    .basket-options {
        font-size: 0.9rem;
        margin-top: 0.5rem;
        padding-left: 1rem;
    }

    .basket-option-line {
        margin-bottom: 0.25rem;
    }

    .basket-price {
        text-align: right;
        font-weight: bold;
        margin-top: 0.5rem;
        font-size: 1rem;
    }

    .basket-total {
        margin-top: 1.5rem;
        text-align: right;
        font-size: 1rem;
    }

        .basket-total strong {
            font-size: 1.1rem;
        }

    .free-delivery {
        color: #2ecc71;
        font-weight: bold;
    }

    .form-input {
        display: block;
        width: 100%;
        margin-bottom: 1rem;
        padding: 0.75rem;
        font-size: 1rem;
        border: 1px solid #ccc;
        border-radius: 6px;
        background: #fff;
        box-sizing: border-box;
    }

    .basket-preview {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: #410330;
        color: white;
        padding: 1rem 1.5rem;
        border-top-left-radius: 1rem;
        border-top-right-radius: 1rem;
        box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.3);
        z-index: 1050;
        cursor: pointer;
    }

    .basket-summary {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 1.1rem;
    }

    .basket-expanded {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        max-height: 90vh;
        background: white;
        z-index: 1100;
        border-top-left-radius: 1rem;
        border-top-right-radius: 1rem;
        box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.2);
        overflow-y: auto;
        animation: slideUp 0.3s ease-out;
    }

    @keyframes slideUp {
        from {
            transform: translateY(100%);
        }

        to {
            transform: translateY(0%);
        }
    }

    .text-main {
        color: #25003e;
    }

    @keyframes pulse {
        0% {
            transform: scale(1, 1);
            box-shadow: 0 0 0 rgba(255, 255, 255, 0.4);
        }

        50% {
            transform: scale(1.025, 1.1); /* wider and taller */
            box-shadow: 0 0 12px rgba(255, 255, 255, 0.6);
        }

        100% {
            transform: scale(1, 1);
            box-shadow: 0 0 0 rgba(255, 255, 255, 0.4);
        }
    }

    .basket-pulse {
        animation: pulse 1s ease;
    }

    
    .qty-button {
        background: #fff;
        border: none;
        border-radius: 4px;
        padding: 4px 8px;
        font-weight: bold;
        font-size: 1rem;
        color: #333;
        transition: background 0.2s;
        width: 50px;
    }

        .qty-button:hover {
            background: #ccc;
        }
</style>